import { Loadable } from '@/components/loaders';
import { ExpressionConditionRender } from '@/features/audienceEdit_v2/components';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetAudienceQuery, useGetPropertyOperationsQuery } from '@/services/endpoints/audiences';
import { mapAudienceExpressionToRules } from '@/utils';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CampaignDetailsSection } from '../CampaignDetailsSection';

type RulesUsedSectionProps = {
  audienceId: string | undefined;
};

export const RulesUsedSection: FC<RulesUsedSectionProps> = ({ audienceId }) => {
  const { t } = useTranslation('audience_details');
  const workspace = useWorkspace();

  const { data: audience, isFetching: isAudienceFetching } = useGetAudienceQuery(
    {
      workspaceId: workspace.id,
      audienceId,
    },
    {
      skip: !audienceId,
    }
  );

  const { data: { propertyOperations } = {} } = useGetPropertyOperationsQuery(
    {
      workspaceId: workspace.id,
      audienceId: audience?.id,
    },
    {
      skip: !audience?.id,
    }
  );

  const rules = useMemo(() => mapAudienceExpressionToRules(audience?.expression), [audience?.expression]);

  return (
    <CampaignDetailsSection header='rulesUsed'>
      <Loadable isLoading={isAudienceFetching}>
        {rules.map((rule, index) => (
          <div key={rule.id} className='flex overflow-hidden bg-white-100 p-4 shadow sm:rounded-md'>
            <h6 className='mr-6 w-28 text-right text-sm text-gray-900'>
              {t(index === 0 ? 'sources_and_rules.rules.users_who' : 'sources_and_rules.rules.and')}
            </h6>
            <div className='flex w-full flex-col space-y-2'>
              <div className='space-y-2 rounded border p-1'>
                {rule.expressions.map((expression, index) => (
                  <div key={index} className='flex items-center p-3'>
                    {index > 0 && <h6 className='mr-2 w-6 text-right text-sm text-gray-900'>{t('sources_and_rules.rules.or')}</h6>}
                    <ExpressionConditionRender
                      expression={expression}
                      isViewMode={true}
                      datasourceIds={audience.datasourceIds}
                      operationList={propertyOperations}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </Loadable>
    </CampaignDetailsSection>
  );
};
